import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { brandInfo } from '@/mocks/site'

const FORM_ENDPOINT = 'https://readdy.ai/api/form/daokgitscq3ovkco3ngg'

export default function ContactSection() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [formError, setFormError] = useState('')

  const serviceOptions = t('contact.serviceOptions', { returnObjects: true }) as string[]

  const contactDetails = [
    { id: 'c1', icon: 'ri-phone-line', label: t('contact.detailCall'), value: brandInfo.phone, href: brandInfo.phoneLink },
    { id: 'c2', icon: 'ri-mail-line', label: t('contact.detailEmail'), value: brandInfo.email, href: `mailto:${brandInfo.email}` },
    { id: 'c3', icon: 'ri-map-pin-2-line', label: t('contact.detailStudios'), value: t('contact.locations'), href: '' },
    { id: 'c4', icon: 'ri-time-line', label: t('contact.detailHours'), value: t('contact.hours'), href: '' },
  ]

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)

    const honeypot = String(formData.get('website_alt') || '').trim()
    if (honeypot) {
      setStatus('success')
      return
    }

    setStatus('submitting')
    setFormError('')

    try {
      const payload = new URLSearchParams()
      formData.forEach((value, key) => {
        if (key === 'website_alt') return
        payload.append(key, String(value))
      })

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      })

      const responseText = await response.text()
      let parsed: Record<string, unknown> | null = null
      try {
        parsed = JSON.parse(responseText)
      } catch {
        parsed = null
      }

      const meta = (parsed?.meta ?? null) as Record<string, unknown> | null
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText
      const code = parsed?.code as string | undefined
      const isSpam = String(serverMsg).toLowerCase().includes('spam')

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success')
        form.reset()
      } else {
        setStatus('error')
        setFormError(String(serverMsg) || t('contact.errorGeneric'))
      }
    } catch {
      setStatus('error')
      setFormError(t('contact.errorNetwork'))
    }
  }

  return (
    <section id="contact" className="w-full bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-primary-500" />
            <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
              {t('contact.label')}
            </span>
            <span className="h-px w-10 bg-primary-500" />
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
            {t('contact.title')}
          </h2>
          <p className="mt-5 text-base text-foreground-600 leading-relaxed">{t('contact.intro')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactDetails.map((item) => (
                <div key={item.id} className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center bg-primary-100 text-primary-700 mb-3">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <div className="text-xs uppercase tracking-wider text-foreground-500 mb-1">{item.label}</div>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-sm font-semibold text-foreground-900 hover:text-primary-700 transition-colors cursor-pointer break-all"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <div className="text-sm font-semibold text-foreground-900">{item.value}</div>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-lg overflow-hidden border border-background-200 h-64 md:h-72">
              <iframe
                title="Shabi Media — based in Tamil Nadu, serving Madurai & Karaikudi"
                src="https://www.google.com/maps?q=Madurai,Tamil%20Nadu&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <span className="w-16 h-16 rounded-full flex items-center justify-center bg-primary-100 text-primary-700 mx-auto mb-5">
                    <i className="ri-check-line text-3xl" />
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                    {t('contact.successTitle')}
                  </h3>
                  <p className="text-sm text-foreground-600 max-w-sm mx-auto">{t('contact.successText')}</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-7 inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-800 text-sm font-medium cursor-pointer hover:border-primary-400 hover:text-primary-700 transition-colors"
                  >
                    <i className="ri-arrow-left-line text-base" />
                    {t('contact.successButton')}
                  </button>
                </div>
              ) : (
                <form id="shabi-media-enquiry-form" data-readdy-form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                    <div>
                      <label htmlFor="enq-name" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formName')}
                      </label>
                      <input
                        id="enq-name"
                        name="name"
                        type="text"
                        required
                        placeholder={t('contact.formNamePh')}
                        className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="enq-phone" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formPhone')}
                      </label>
                      <input
                        id="enq-phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder={t('contact.formPhonePh')}
                        className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="enq-email" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formEmail')}
                      </label>
                      <input
                        id="enq-email"
                        name="email"
                        type="email"
                        required
                        placeholder={t('contact.formEmailPh')}
                        className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="enq-date" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formDate')}
                      </label>
                      <input
                        id="enq-date"
                        name="event_date"
                        type="date"
                        className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enq-service" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formService')}
                      </label>
                      <select
                        id="enq-service"
                        name="service"
                        className="w-full h-12 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 cursor-pointer"
                      >
                        {serviceOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enq-message" className="block text-xs uppercase tracking-wider text-foreground-600 mb-2">
                        {t('contact.formMessage')}
                      </label>
                      <textarea
                        id="enq-message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        placeholder={t('contact.formMessagePh')}
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 resize-none"
                      />
                    </div>
                  </div>

                  <input
                    className="form-extra-field-name"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === 'error' && formError && (
                    <p className="mt-4 text-sm text-accent-700 flex items-center gap-2">
                      <i className="ri-error-warning-line text-lg" />
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="mt-6 inline-flex items-center justify-center gap-2 h-12 w-full sm:w-auto px-8 rounded-md bg-primary-500 text-background-50 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" />
                        {t('contact.sending')}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-lg" />
                        {t('contact.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}